import React from 'react';

import { Button } from '@/design-system/button';
import { IconButton } from '@/design-system/icon-button';
import type { ZoneDriver } from '@/services/api/zoneDrivers';

interface ZoneDriverBadgeProps {
  /** Roster size. 0 renders the amber "no mitra yet" state. */
  count: number;
  /** Zone color — the badge borrows it so chip ↔ territory reads as one thing. */
  color: string;
  /** Zone code, used for the accessible label. */
  code: string;
  /** True while this badge's popover is open (pressed styling). */
  open: boolean;
  onClick: () => void;
}

/**
 * Floating chip pinned to a zone's centroid showing how many drivers cover it.
 * The UI calls them "mitra"; the data layer calls them drivers.
 * Mounted by `ZoneMap` inside a Google Maps overlay, so it pans and zooms with
 * the territory it labels.
 *
 * A zone with no driver is a valid (if unfinished) state, so it gets an amber
 * dashed chip rather than being hidden — an empty roster you can't see is an
 * empty roster nobody fills in.
 */
export const ZoneDriverBadge: React.FC<ZoneDriverBadgeProps> = ({
  count,
  color,
  code,
  open,
  onClick,
}) => {
  const empty = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={`${code} — ${count} mitra`}
      title={`${code} — ${count} mitra`}
      className={`inline-flex cursor-pointer items-center gap-1 rounded-full py-0.5 pl-1 pr-1.5 text-[11px] font-bold leading-none tabular-nums shadow-sm transition-transform hover:scale-[1.08] ${
        empty
          ? 'border border-dashed border-warning-base bg-bg-white-0 text-warning-base'
          : 'border text-static-white'
      } ${open ? 'scale-[1.12] ring-2 ring-white' : ''}`}
      style={
        empty
          ? undefined
          : { background: color, borderColor: 'rgba(255,255,255,0.9)' }
      }
    >
      <i className="ri-user-fill text-[13px]" aria-hidden="true" />
      {empty ? '0' : count}
    </button>
  );
};

interface ZoneDriverPopoverProps {
  code: string;
  name: string;
  color: string;
  drivers: ZoneDriver[];
  /** True while the hub-wide roster fetch is still in flight. */
  isLoading?: boolean;
  onClose: () => void;
  onManage: () => void;
}

/**
 * Roster read-out that hangs above a badge. Renders straight from the hub-wide
 * `zone-drivers` payload — clicking a badge never triggers a fetch.
 *
 * Positioned `absolute` inside a zero-size overlay host, so `bottom-full` +
 * `mb-5` lands it just above the chip regardless of map zoom.
 */
export const ZoneDriverPopover: React.FC<ZoneDriverPopoverProps> = ({
  code,
  name,
  color,
  drivers,
  isLoading = false,
  onClose,
  onManage,
}) => (
  <div className="absolute bottom-full left-1/2 mb-5 w-[268px] -translate-x-1/2 overflow-hidden rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-md">
    <div className="flex items-start gap-2 border-b border-stroke-soft-200 px-3 py-2">
      <span
        className="mt-0.5 h-3 w-3 shrink-0 rounded-full border border-stroke-soft-200"
        style={{ backgroundColor: color }}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-semibold tracking-tight text-text-strong-950">
          {code}
        </p>
        <p className="truncate text-[11px] text-text-sub-600">{name}</p>
      </div>
      <IconButton
        type="button"
        tone="neutral"
        style="ghost"
        size="xs"
        onClick={onClose}
        aria-label="Close mitra list"
      >
        <i className="ri-close-line" />
      </IconButton>
    </div>

    <div className="max-h-[196px] overflow-y-auto">
      {isLoading ? (
        <p className="px-3 py-3 text-[11.5px] text-text-sub-600">
          Loading mitra…
        </p>
      ) : drivers.length === 0 ? (
        <div className="px-3 py-3">
          <p className="text-[11.5px] font-semibold tracking-tight text-warning-base">
            No mitra assigned yet
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-text-sub-600">
            Dispatch falls back to distance and load for this zone.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-stroke-soft-200">
          {drivers.map((d) => (
            <li key={d.driverId} className="flex items-start gap-2 px-3 py-1.5">
              <span className="mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-full bg-bg-weak-50 text-[10px] font-bold tabular-nums text-text-sub-600">
                {d.priority}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] tracking-tight text-text-strong-950">
                  {d.driverName}
                </span>
                <span className="block truncate font-mono text-[10px] tabular-nums text-text-soft-400">
                  {d.driverCode}
                </span>
                {d.clients.length > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {d.clients.map((c) => (
                      <span
                        key={c.id}
                        className="rounded bg-bg-weak-50 px-1 py-px text-[9px] font-semibold tracking-tight text-text-sub-600"
                      >
                        {c.name}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>

    <div className="flex items-center justify-between gap-2 border-t border-stroke-soft-200 bg-bg-weak-50/60 px-3 py-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-soft-400">
        P1 goes first
      </span>
      <Button size="xs" onClick={onManage} className="h-6 px-2 text-[11px]">
        Manage
      </Button>
    </div>
  </div>
);
